import type { MessageKey } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * `pluralCategory` rather than `count === 1`, even in the English file. The rule is the same
 * one the rest of the catalogue follows (D-19): a two-branch check is already wrong at zero in
 * Portuguese, and a helper that differs between the two locales is a helper somebody will
 * copy in the wrong direction. The locale is a literal here because this file IS English.
 */
const plural = (count: number, forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }): string =>
  forms[pluralCategory('en', count)] ?? forms.other

/**
 * What the systems domain's refusals say, in English (the source of truth AND the type).
 *
 * Connected systems, remote agents and runtime policies — the administrator plane's three
 * registries — because they share a reader and a vocabulary. `MCP` never appears in a sentence
 * here: the consumer word is "connected system" (PRD-23), and the acronym stays in the English
 * `message` where an operator is reading a log.
 */
export const systems: Pick<ServerMessages, Extract<MessageKey, `systems.${string}`>> = {
  // --- a connected system --------------------------------------------------

  'systems.not_found': () => 'No connected system with that id is registered.',
  'systems.name_taken': ({ name }) => `A connected system called “${name}” already exists.`,
  'systems.changed_by_someone_else': () =>
    'Somebody else changed this connected system. Reload it and reapply your change.',
  'systems.if_match_invalid': () => 'If-Match has to be the version number you last read.',
  'systems.url_invalid': () =>
    'That endpoint is not a URL this platform can use. Give an absolute https:// address with no credentials in it.',
  'systems.url_not_routable': () =>
    'That address is private, loopback or link-local, and this platform will not dial inside its own network. ' +
    'Register an endpoint that is reachable from outside.',

  // --- what to change about one that will not connect ----------------------
  //
  // The connect flow's advice line. The probe reports six verdicts and one of them failed;
  // this is the sentence under the ladder that says what to go and do about it.
  //
  // Sixteen of them, and they are not a translation of the stage names: `url_invalid` alone
  // is five distinct things to fix. Collapsing them would undo the one thing this screen is
  // for, which is that a bad address, an unreachable host and a missing credential have three
  // different owners and “connection failed” sends everybody to the wrong one (MCP-025).
  //
  // The acronym stays out of every one of them, per PRD-23: the word a person reads is
  // “connected system”, on this screen as on every other. `/mcp` survives in one sentence
  // because it is a path, which is data.

  'systems.hint_url_not_absolute': () => 'Use an absolute https:// URL.',
  'systems.hint_url_has_credentials': () =>
    'Remove the “user:password@” part and register the credential as a token reference instead.',
  'systems.hint_url_plaintext': () =>
    'Plaintext HTTP would expose the bearer token in transit. Use https, or set MCP_ALLOW_INSECURE_URLS=true for local development only.',
  'systems.hint_url_transport': () => 'Only https (and http in development) are supported transports.',
  'systems.hint_host_unresolved': () =>
    'Check the spelling, and that this deployment can see the DNS zone that serves it.',
  'systems.hint_address_private': () =>
    'Register a routable external endpoint, or run the system behind a reverse proxy with a public name.',
  'systems.hint_address_resolves_private': () =>
    'The platform will not dial internal addresses. Publish the system on a routable endpoint.',
  'systems.hint_url_invalid': () => 'Fix the address on this connected system.',
  'systems.hint_ssrf_blocked': () => 'This platform will not dial private, loopback or link-local addresses.',
  'systems.hint_token_unresolved': () =>
    'The registry stores a reference, not a token. Set the referenced environment variable (or configure the credential store) and try again.',
  'systems.hint_connect': () =>
    'The endpoint could not be reached. Check DNS, TLS and egress policy — this is a network fault, not a protocol one.',
  'systems.hint_version_mismatch': () =>
    'No protocol revision is shared. Upgrade the system, or wait for this platform to add the revision it speaks.',
  'systems.hint_authorization_required': () =>
    'The system requires a credential it did not receive. Connect an account, or supply a token reference.',
  'systems.hint_sign_in_with_account': () =>
    'This system issues credentials through an authorization server. Connect an account rather than pasting a token.',
  'systems.hint_discover': () =>
    'The endpoint answered, and not as a connected system. Confirm this exact URL is the system’s endpoint rather than a documentation page or a base path.',
  'systems.hint_not_an_endpoint': () =>
    'That address answered, but not as a connected system’s endpoint. It is usually a documentation page or the site root — the endpoint is often at a path such as /mcp.',
  'systems.hint_tools_list': () =>
    'The system was discovered cleanly but could not list its tools. Check the credential’s scopes.',

  // --- its capabilities ----------------------------------------------------

  'systems.capability_not_found': () => 'No connected system has a capability with that id.',
  'systems.capability_quarantined': () =>
    'This capability is quarantined after a schema change. Review the new schema and clear the quarantine ' +
    'before switching it on.',
  'systems.tool_arguments_rejected': ({ tool }) => `Those arguments are not what “${tool}” declared it accepts.`,

  // --- connecting an account to one ----------------------------------------

  'systems.sign_in_not_configured': ({ name }) =>
    `“${name}” has not said which authority issues its credentials, so there is nothing to sign in to yet. ` +
    'Check the system first.',
  // "Already completed" and "took too long" are `auth.sign_in_not_pending` and
  // `auth.sign_in_expired` — the same two sentences the connector flow says.
  'systems.sign_in_incomplete': () => 'This sign-in did not come back complete. Start it again.',
  'systems.sign_in_refused': ({ issuer, error }) => `${issuer} refused the sign-in (${error}).`,
  'systems.connection_not_found': () => 'You have no connection with that id.',
  'systems.service_account_not_connected': () =>
    'No account is connected to this system yet. An administrator connects it once, for the whole deployment.',
  'systems.service_account_expired': () =>
    'The account connected to this system has expired and cannot be renewed automatically. ' +
    'An administrator has to connect it again.',
  'systems.service_account_requires_admin': () =>
    'Connecting an account the whole deployment uses needs the administrator role.',
  'systems.your_account_not_connected': () => 'You have not connected your account to this system yet.',
  'systems.your_connection_expired': () => 'Your connection to this system has expired. Connect it again.',
  'systems.needs_a_person': ({ name }) =>
    `“${name}” runs as each person's own account, and this call has nobody to be.`,
  'systems.credential_reference_missing': ({ name }) =>
    `“${name}” expects a credential but carries no reference to one.`,

  // --- a remote agent ------------------------------------------------------

  'systems.remote_agent_not_found': () => 'No remote agent with that id is registered.',
  'systems.remote_agent_slug_taken': ({ slug }) => `A remote agent with the slug “${slug}” is already registered.`,
  'systems.remote_agent_changed': () => 'Somebody else changed this remote agent. Reload it and reapply your edit.',
  'systems.remote_agent_card_missing': () =>
    'This agent has no fetched card yet. Refresh the card before switching it on.',
  'systems.remote_agent_card_changed': () =>
    'This agent’s card changed since you reviewed it. Review the current card before switching it on.',
  'systems.remote_agent_no_interface': () =>
    'This agent’s card advertises no HTTP+JSON interface, so the platform has no way to call it.',

  // --- a runtime policy ----------------------------------------------------

  'systems.policy_not_found': () => 'That policy no longer exists.',
  'systems.policy_changed': () => 'Somebody else changed this policy. Reload it and reapply your change.',
  'systems.policy_name_taken': () => 'Another policy already uses that name. Pick a different one.',
  'systems.policy_not_written': () => 'The policy was not written. Try again.',
  'systems.policy_settings_invalid': ({ name }) => `The stored settings for “${name}” are not valid.`,
  /**
   * Names the agents rather than only counting them: "3 agents use this" tells an administrator
   * they are blocked, the list tells them what to reassign, which is the next thing they were
   * going to ask. The count is bounded at 25 by the query behind it, so it needs no grouping.
   */
  'systems.policy_still_assigned': ({ count, agents }) =>
    plural(count, {
      one: `One agent still uses this policy (${agents}). Move it to another policy first.`,
      other: `${count} agents still use this policy (${agents}). Move them to another policy first.`
    }),
  'systems.builtin_policy_keeps_its_name': ({ name }) =>
    `“${name}” is the built-in policy and keeps its name. Copy it to a new policy if you want a differently named one.`,
  'systems.builtin_policy_not_removable': ({ name }) =>
    `“${name}” is the built-in policy and cannot be removed. Every agent falls back to it.`,
  'systems.policy_agent_not_configured': ({ agent }) => `No agent called “${agent}” is configured.`,
  'systems.policy_assignment_not_written': () => 'The policy assignment was not written. Try again.'
}
